import { Injectable, computed, signal } from '@angular/core';
import { SyndicBuildingProjection } from '../../../../../shared/definitions/syndic';

/**
 * Route-scoped context for "is this owner building managed by a connected
 * syndic?". Provided on the `building/:id` route so the shell and every child
 * tab (charges, expenses, units…) share ONE resolution.
 *
 * The shell (`BuildingDetailComponent`) resolves the syndic link once from the
 * `syndic_buildings` projection and pushes it here via {@link setLink}; child
 * tabs only READ the signals to render read-only consistently. When a building
 * is syndic-managed, the syndic owns the charges/expenses/quotités — the owner
 * surfaces them in lecture seule.
 */
@Injectable()
export class BuildingSyndicContextService {
  /** The linked syndic building projection, or null when none is connected. */
  readonly link = signal<SyndicBuildingProjection | null>(null);
  /** False until the first resolution lands — avoids gating on unknown state. */
  readonly resolved = signal(false);

  /** True only once resolved AND a link exists. */
  readonly isSyndicManaged = computed(() => this.resolved() && this.link() !== null);

  /** The connected syndic's display name, when available. */
  readonly syndicName = computed(() => this.link()?.syndicContact?.name ?? null);

  /** Called by the building-detail shell each time the syndic link resolves. */
  setLink(link: SyndicBuildingProjection | null): void {
    this.link.set(link);
    this.resolved.set(true);
  }
}
